import React, { useContext, useEffect } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import Select from "react-select";

import FormItem from "app/components/inputs/FormItem";
import { durationCustomStyles, DropdownIndicator } from "app/components/PaymentSelector";
import { UserContext, RoleType } from "app/contexts/UserContext";
import { OnlinePaymentTypeOptions, getChargeTimingOptions } from "./paymentHelpers";
import PayingAthleteSelect from "./PayingAthleteSelect";
import PackageSelect from "./PackageSelect";
import PaymentStatusSelect from "./PaymentStatusSelect";
import styles from "./styles.module.scss";
// CN-1323: family-payer resolution (a managed dependent is billed to its head)
// is shared with the program-registration flow — see app/utils/familyPayer.
import { soleFamilyHead } from "app/utils/familyPayer";

interface PaymentFieldsProps {
  selectedAthletes: any[];
  availablePackages: any[];
  paymentEditable: boolean;
  isEdit?: boolean;
  watchPaymentMethod: string;
  watchPayingAthlete: any;
  isCheckout: boolean;
  shouldShowPayingAthlete: boolean;
  // The paying athlete's saved-card status at the coach's connected Stripe
  // account: `true` = has a card, `false` = confirmed none, `null` = unknown
  // (lookup in flight). Offer card_on_file only once confirmed `true`; strip an
  // existing card_on_file selection only on a confirmed `false` — never while
  // `null`, so editing a card-on-file session isn't silently flipped mid-lookup.
  cardOnFileStatus?: boolean | null;
  // CN-1323 (CFTB-3320): true while the participant's packages are being
  // fetched. The payment method auto-flips to "package" once they resolve, so
  // until then we show a loader instead of the (possibly wrong) payment fields
  // — otherwise the "paying parent" note flashes for a family member who in
  // fact has a valid package.
  isLoadingPackages?: boolean;
}

export default function PaymentFields({
  selectedAthletes,
  availablePackages,
  paymentEditable,
  isEdit,
  watchPaymentMethod,
  watchPayingAthlete,
  isCheckout,
  shouldShowPayingAthlete,
  cardOnFileStatus = null,
  isLoadingPackages = false,
}: PaymentFieldsProps) {
  const user = useContext(UserContext);
  const { control, watch, setValue } = useFormContext();
  const { t } = useTranslation();
  const watchOnlinePaymentType = watch('online_payment_type') ?? OnlinePaymentTypeOptions[0]?.value;
  // CN-2515: the bill-everything-upfront option only applies when creating a
  // repeating series — edits go through per-lesson purchases.
  const watchRepeat = watch('repeat');
  const watchChargeTiming = watch('charge_timing');
  const offerAllNow = !isEdit && !!watchRepeat;
  const timingOptions = getChargeTimingOptions(watchOnlinePaymentType, offerAllNow);

  // Snap back if the repeat rule is removed while all-now is selected.
  useEffect(() => {
    if (!offerAllNow && watchChargeTiming === 'all_immediate') {
      setValue('charge_timing', 'immediate', { shouldValidate: true });
    }
  }, [offerAllNow, watchChargeTiming, setValue]);

  const isCurrentlyCardOnFile = watchOnlinePaymentType === 'card_on_file';
  // Offer card_on_file once the athlete is confirmed to have a saved card; while
  // editing a session that already uses it, keep it selectable until the lookup
  // definitively reports no card (`false`) — never drop it on `null` (in flight).
  const showCardOnFile = cardOnFileStatus === true || (isCurrentlyCardOnFile && cardOnFileStatus !== false);

  // Strip an existing card_on_file selection only on a CONFIRMED no-card
  // (`false`), never while the lookup is in flight (`null`) — otherwise editing
  // a card-on-file session silently flips to send_invoice before the check
  // resolves.
  useEffect(() => {
    if (cardOnFileStatus === false && isCurrentlyCardOnFile) {
      setValue('online_payment_type', OnlinePaymentTypeOptions[0]?.value, { shouldValidate: true });
    }
  }, [cardOnFileStatus, isCurrentlyCardOnFile, setValue]);

  // CN-1323 (CFTB-3284): a managed child can't be billed (no Stripe customer),
  // so the family head is the payer. The paying-athlete dropdown only appears for
  // groups, so for a single child surface a read-only note naming the parent.
  const soleDependent =
    selectedAthletes.length === 1 && selectedAthletes[0]?.headExternalId
      ? selectedAthletes[0]
      : null;

  // CN-1323 (CFTB-3295): a group whose participants all belong to one family is
  // billed to that family's head — the payer is fixed, so lock it like the
  // single-dependent case instead of offering an editable dropdown.
  const familyHead = soleFamilyHead(selectedAthletes);

  // The payer is fixed (single dependent or one-family group) → show the
  // read-only note naming the head and don't render the editable selector. The
  // family head is itself a selected athlete, so read its name directly; the
  // single-dependent case has no head profile in the list, so fall back to its
  // serialized headFullName.
  const lockedPayer = familyHead ?? soleDependent;

  // CFTB-3316: when the payer is NOT fixed — a mixed group (family members +
  // other athletes), siblings without their parent, or members of two families —
  // the editable paying-athlete dropdown is shown. If the coach picks a managed
  // dependent as the paying athlete, the bill still routes to that dependent's
  // head, so surface the same note naming the parent / guardian.
  const chosenPayer =
    !lockedPayer && watchPayingAthlete
      ? selectedAthletes.find((a) => a.id === watchPayingAthlete)
      : null;
  const dependentPayer = chosenPayer?.headExternalId ? chosenPayer : null;

  const notePayer = lockedPayer ?? dependentPayer;
  const notePayerName =
    familyHead?.fullName ||
    soleDependent?.headFullName ||
    dependentPayer?.headFullName ||
    t("Parent / Guardian");

  const payingAthleteSelect =
    shouldShowPayingAthlete && !familyHead ? (
      <PayingAthleteSelect selectedAthletes={selectedAthletes} paymentEditable={paymentEditable} />
    ) : null;

  // Lock the submitted paying athlete to the head so the booking is billed to
  // them, matching what the single-dependent path resolves to server-side.
  useEffect(() => {
    if (familyHead && watchPayingAthlete !== familyHead.id) {
      setValue("paying_athlete_id", familyHead.id, { shouldValidate: true });
    }
  }, [familyHead, watchPayingAthlete, setValue]);

  // CN-1323 (CFTB-3320): the method auto-flips to "package" only after packages
  // load, so suppress the "paying parent" note while that fetch is in flight —
  // otherwise it flashes for a family member who actually has a valid package.
  // CFTB-3336: gate ONLY the note on the loading flag, never the whole field set.
  // PayingAthleteSelect registers paying_athlete_id with shouldUnregister, so
  // unmounting it mid-load (a load the selection itself triggers) wiped the
  // chosen payer and the booking failed to save.
  const payingParentNote =
    !isLoadingPackages && notePayer && (watchPaymentMethod === "online" || watchPaymentMethod === "offline") ? (
      <div className={styles.paying_parent_note}>
        <span className={styles.paying_parent_label}>{t("Paying User")}:</span>{" "}
        <strong>{notePayerName}</strong>
        <div className={styles.paying_parent_hint}>
          {t("Family member sessions are billed to the parent / guardian.")}
        </div>
      </div>
    ) : null;

  const packageSelect = watchPayingAthlete && watchPaymentMethod === 'package' ? (
    <PackageSelect availablePackages={availablePackages} paymentEditable={paymentEditable} />
  ) : null;

  const showPaymentStatus = isEdit || watchPaymentMethod === 'offline';
  const paymentStatusSelect = showPaymentStatus ? (
    <PaymentStatusSelect paymentEditable={paymentEditable} watchPaymentMethod={watchPaymentMethod} />
  ) : null;

  const onlinePaymentTypeSelect = (
    <FormItem className={styles.input_field}>
      <Controller
        name="online_payment_type"
        control={control}
        render={({ field }) => (
          <Select
            {...field}
            value={OnlinePaymentTypeOptions.find(opt => opt.value === field.value) ?? OnlinePaymentTypeOptions[0]}
            onChange={(option: any) => field.onChange(option?.value)}
            // TODO: Re-enable card_on_file for schedulers once payment flow is finalized
            options={OnlinePaymentTypeOptions.filter(opt =>
              opt.value !== 'checkout' &&
              !(opt.value === 'card_on_file' && user.role === RoleType.SCHEDULER) &&
              !(opt.value === 'card_on_file' && !showCardOnFile)
            )}
            isDisabled={isCheckout || !paymentEditable}
            styles={durationCustomStyles}
            placeholder={t("Payment Type")}
            components={{ IndicatorSeparator: () => null, DropdownIndicator }}
            isSearchable={false}
            menuPortalTarget={document.body}
            menuPlacement="auto"
            classNamePrefix="rs"
          />
        )}
      />
    </FormItem>
  );

  const chargeTimingSelect = (
    <FormItem className={styles.input_field}>
      <Controller
        name="charge_timing"
        control={control}
        render={({ field }) => (
          <Select
            {...field}
            value={timingOptions.find(opt => opt.value === field.value) ?? timingOptions[0]}
            onChange={(option: any) => field.onChange(option?.value)}
            options={timingOptions}
            isDisabled={isCheckout || !paymentEditable}
            styles={durationCustomStyles}
            placeholder={t("Charge Timing")}
            components={{ IndicatorSeparator: () => null, DropdownIndicator }}
            isSearchable={false}
            menuPortalTarget={document.body}
            menuPlacement="auto"
            classNamePrefix="rs"
          />
        )}
      />
    </FormItem>
  );

  return (
    <>
      {payingParentNote}

      {/* Online sub-fields */}
      {watchPaymentMethod === 'online' && (
        <div className={styles.sub_row_wrap}>
          {payingAthleteSelect && (
            <div className={styles.sub_row_item_half}>{payingAthleteSelect}</div>
          )}
          <div className={styles.sub_row_item_half}>{onlinePaymentTypeSelect}</div>
          {!(isEdit && isCheckout) && (
            <div className={styles.sub_row_item_half}>{chargeTimingSelect}</div>
          )}
          {paymentStatusSelect && (
            <div className={styles.sub_row_item_half}>{paymentStatusSelect}</div>
          )}
        </div>
      )}

      {/* Offline sub-fields */}
      {watchPaymentMethod === 'offline' && (
        <>
          {payingAthleteSelect ? (
            <div className={styles.sub_row}>
              <div className={styles.sub_row_item_half}>{payingAthleteSelect}</div>
              {paymentStatusSelect && (
                <div className={styles.sub_row_item_half}>{paymentStatusSelect}</div>
              )}
            </div>
          ) : paymentStatusSelect ? (
            <div className={styles.sub_row}>
              <div className={styles.sub_row_item_half}>{paymentStatusSelect}</div>
              <div className={styles.sub_row_spacer} />
            </div>
          ) : null}
        </>
      )}

      {/* Package sub-fields */}
      {watchPaymentMethod === 'package' && (
        <>
          {payingAthleteSelect && (
            <div className={styles.sub_row}>
              <div className={styles.sub_row_item}>{payingAthleteSelect}</div>
              <div className={styles.sub_row_spacer} />
            </div>
          )}
          {packageSelect && (
            <div className={styles.sub_row}>
              <div className={styles.sub_row_item}>{packageSelect}</div>
              <div className={styles.sub_row_spacer} />
            </div>
          )}
        </>
      )}
    </>
  );
}
